/**
 * Onboarding-completeness banner logic (Notion #67).
 *
 * A user who hit "Save & Exit" during onboarding has `onboarding_deferred=true`
 * and reaches the dashboard (soft-gate in `src/app/index.tsx`). The dashboard
 * then shows a resume nudge so they can finish. This module owns the pure
 * show/hide + progress derivation so it's unit-testable independent of the UI.
 */
import {
  STARTUP_ONBOARDING_STEP_IDS,
  INVITEE_ONBOARDING_STEP_IDS,
  STEP_LABELS,
  resolveResumeStep,
  type OnboardingStep,
} from './steps';

export type OnboardingBannerInput = {
  /** profiles.onboarding_completed */
  onboardingCompleted: boolean;
  /** whether a DB profile has loaded (never nudge before we know the real state) */
  hasProfile: boolean;
  /** resolved role; admins/pending are never nudged */
  role: string | null | undefined;
};

/**
 * Show the resume banner only once we have a real profile, the user is a
 * founder/investor, and onboarding is genuinely unfinished. Returns false
 * during the pre-profile load window so a completed user never sees a flash.
 */
export function shouldShowOnboardingBanner(input: OnboardingBannerInput): boolean {
  if (!input.hasProfile) return false;
  if (input.onboardingCompleted) return false;
  return input.role === 'startup' || input.role === 'investor';
}

export type OnboardingProgress = {
  resumeStep: OnboardingStep;
  resumeLabel: string;
  /** 1-based position of the resume step in the flow */
  current: number;
  total: number;
};

/**
 * Derive the resume step + progress from the persisted `onboarding_step`
 * pointer. Uses the invitee list when applicable (founders and investors both
 * use the full list; the invitee list is the short join flow).
 */
export function computeOnboardingProgress(
  persistedStep: string | null | undefined,
  isInvitee: boolean,
): OnboardingProgress {
  // Exclude the `complete`/result screen — it is not a functional step (proposal
  // §Interaction), so the banner reads "Step X of 4", consistent with the
  // onboarding screens' STARTUP_ONBOARDING_DISPLAY_STEPS count.
  const list: OnboardingStep[] = (
    isInvitee ? INVITEE_ONBOARDING_STEP_IDS : STARTUP_ONBOARDING_STEP_IDS
  ).filter((id) => id !== 'complete');
  const resumeStep = resolveResumeStep(persistedStep, isInvitee);
  const idx = list.indexOf(resumeStep);
  return {
    resumeStep,
    resumeLabel: STEP_LABELS[resumeStep],
    current: idx + 1,
    total: list.length,
  };
}
